import React, { useEffect, useRef, useState } from 'react';
import './TetrisPanel.css'; // Import the CSS file
import { usePanels } from './App';

const COLS = 10;
const ROWS = 15;

const TETROMINOES = {
  R: [
    [0, 0, 0, 0],
    [1, 1, 1, 1],
    [0, 0, 0, 0],
    [0, 0, 0, 0],
  ],
  S: [
    [0, 1, 1],
    [1, 1, 0],
    [0, 0, 0],
  ],
  T: [
    [0, 1, 0],
    [1, 1, 1],
    [0, 0, 0],
  ],
  Z: [
    [1, 1, 1],
    [0, 0, 1],
    [0, 0, 0],
  ],
};
const COLORS = {
  R: '#29C3F4',
  S: '#E12455',
  T: '#E4B23D',
  Z: '#2DB67B',
};
const TETROMINO_KEYS = Object.keys(TETROMINOES);

function randomTetromino() {
  const key = TETROMINO_KEYS[Math.floor(Math.random() * TETROMINO_KEYS.length)];
  return { shape: TETROMINOES[key], type: key };
}

function rotate(matrix) {
  // Rotate 90 deg clockwise
  return matrix[0].map((_, i) => matrix.map(row => row[i]).reverse());
}

function checkCollision(board, shape, pos) {
  for (let y = 0; y < shape.length; y++) {
    for (let x = 0; x < shape[y].length; x++) {
      if (shape[y][x]) {
        const newY = pos.y + y;
        const newX = pos.x + x;
        if (
          newX < 0 ||
          newX >= COLS ||
          newY >= ROWS ||
          (newY >= 0 && board[newY][newX])
        ) {
          return true;
        }
      }
    }
  }
  return false;
}

function merge(board, shape, pos, type) {
  const newBoard = board.map(row => row.slice());
  for (let y = 0; y < shape.length; y++) {
    for (let x = 0; x < shape[y].length; x++) {
      if (shape[y][x]) {
        const newY = pos.y + y;
        const newX = pos.x + x;
        if (newY >= 0 && newY < ROWS && newX >= 0 && newX < COLS) {
          newBoard[newY][newX] = type;
        }
      }
    }
  }
  return newBoard;
}

function clearLines(board) {
  const newBoard = board.filter(row => row.some(cell => !cell));
  while (newBoard.length < ROWS) {
    newBoard.unshift(Array(COLS).fill(null));
  }
  return newBoard;
}

function Controls({ onMoveLeft, onMoveRight, onRotate, onSoftDrop }) {
  return (
    <div className="controls-container">
      <button className="control-button" onClick={onRotate} aria-label="Rotate">⬆</button>
      <div style={{ display: 'flex' }}>
        <button className="control-button" onClick={onMoveLeft} aria-label="Move Left">⬅</button>
        <button className="control-button" onClick={onSoftDrop} aria-label="Soft Drop">⬇</button>
        <button className="control-button" onClick={onMoveRight} aria-label="Move Right">➡</button>
      </div>
      {/* Optional: Add Hard Drop button later if needed */}
      {/* <button className="control-button hard-drop" onClick={onHardDrop} aria-label="Hard Drop">SPACE</button> */}
    </div>
  );
}

function TetrisGame({ panelWidth }) {
  const BLOCK_SIZE = panelWidth ? panelWidth / COLS : 24;
  const [board, setBoard] = useState(() => Array.from({ length: ROWS }, () => Array(COLS).fill(null)));
  const [current, setCurrent] = useState(() => {
    const t = randomTetromino();
    return { ...t, pos: { x: 3, y: -t.shape.length + 1 } };
  });
  const [gameOver, setGameOver] = useState(false);
  const requestRef = useRef();
  const dropInterval = useRef(500);
  const lastDropTime = useRef(Date.now());

  // Handle falling
  useEffect(() => {
    if (gameOver) return;
    function update() {
      const now = Date.now();
      if (now - lastDropTime.current > dropInterval.current) {
        lastDropTime.current = now;
        move(0, 1);
      }
      requestRef.current = requestAnimationFrame(update);
    }
    requestRef.current = requestAnimationFrame(update);
    return () => cancelAnimationFrame(requestRef.current);
    // eslint-disable-next-line
  }, [current, gameOver]);

  // Keyboard controls
  useEffect(() => {
    if (gameOver) return;
    function handle(e) {
      if (e.repeat) return;
      if (e.key === 'ArrowLeft') move(-1, 0);
      else if (e.key === 'ArrowRight') move(1, 0);
      else if (e.key === 'ArrowDown') move(0, 1);
      else if (e.key === 'ArrowUp') rotateTetromino();
      else if (e.key === ' ') hardDrop();
    }
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
    // eslint-disable-next-line
  }, [current, board, gameOver]);

  function move(dx, dy) {
    const newPos = { x: current.pos.x + dx, y: current.pos.y + dy };
    if (!checkCollision(board, current.shape, newPos)) {
      setCurrent(c => ({ ...c, pos: newPos }));
    } else if (dy === 1) {
      // Landed
      if (current.pos.y < 0) {
        setGameOver(true);
        return;
      }
      const merged = merge(board, current.shape, current.pos, current.type);
      const cleared = clearLines(merged);
      setBoard(cleared);
      const t = randomTetromino();
      setCurrent({ ...t, pos: { x: 3, y: -t.shape.length + 1 } });
    }
  }

  function rotateTetromino() {
    const rotated = rotate(current.shape);
    if (!checkCollision(board, rotated, current.pos)) {
      setCurrent(c => ({ ...c, shape: rotated }));
    }
  }

  function hardDrop() {
    let dropY = current.pos.y;
    while (!checkCollision(board, current.shape, { x: current.pos.x, y: dropY + 1 })) {
      dropY++;
    }
    setCurrent(c => ({ ...c, pos: { ...c.pos, y: dropY } }));
    setTimeout(() => move(0, 1), 0); // Land immediately
  }

  function renderCell(cell, x, y) {
    let color = cell ? COLORS[cell] : '#FBF0D9';
    // Draw current tetromino
    if (current) {
      const relY = y - current.pos.y;
      const relX = x - current.pos.x;
      if (
        relY >= 0 &&
        relY < current.shape.length &&
        relX >= 0 &&
        relX < current.shape[0].length &&
        current.shape[relY][relX]
      ) {
        color = COLORS[current.type];
      }
    }
    return (
      <div
        key={x}
        style={{
          width: BLOCK_SIZE,
          height: BLOCK_SIZE,
          background: color,
          border: '1px solid #ccc',
          boxSizing: 'border-box',
        }}
      />
    );
  }

  return (
    <div style={{ width: panelWidth, margin: '0 auto', paddingTop: 24, position: 'relative' }}>
      <Controls
        onMoveLeft={() => move(-1, 0)}
        onMoveRight={() => move(1, 0)}
        onRotate={rotateTetromino}
        onSoftDrop={() => move(0, 1)}
      />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${COLS}, ${BLOCK_SIZE}px)`,
          background: '#222',
          // border: '2px solid #888',
          width: COLS * BLOCK_SIZE,
          height: ROWS * BLOCK_SIZE,
          margin: '0 auto',
        }}
      >
        {board.map((row, y) =>
          row.map((cell, x) => renderCell(cell, x, y))
        )}
      </div>
      {gameOver && (
        <div style={{ textAlign: 'center', color: 'red', marginTop: 16, display: 'none' }}>
          <h3>Game Over</h3>
          <button onClick={() => {
            setBoard(Array.from({ length: ROWS }, () => Array(COLS).fill(null)));
            const t = randomTetromino();
            setCurrent({ ...t, pos: { x: 3, y: -t.shape.length + 1 } });
            setGameOver(false);
          }}>Restart</button>
        </div>
      )}
      <div style={{ textAlign: 'center', marginTop: 8, color: '#666', fontSize: 14, display: 'none' }}>
        <div>← →: Move | ↑: Rotate | ↓: Soft Drop | Space: Hard Drop</div>
      </div>
    </div>
  );
}

export default function TetrisPanel() {
  const panelRef = useRef(null);
  const [panelWidth, setPanelWidth] = useState(0);
  const [usableWidth, setUsableWidth] = useState(0);
  const { swapPanel } = usePanels();

  useEffect(() => {
    function updateWidth() {
      if (panelRef.current) {
        const width = panelRef.current.offsetWidth;
        setPanelWidth(width);
        // Get 1rem in px
        const remInPx = parseFloat(getComputedStyle(document.documentElement).fontSize);
        setUsableWidth(width);
      }
    }
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Calculate the grid size
  const blockSize = usableWidth / COLS;
  
  return (  
    <div 
      id="tetrisPanel"
      className="Panel Panel-right Panel-tetris" 
      ref={panelRef}
      style={{
        position: 'relative',
        backgroundImage: `
          linear-gradient(to right, #ccc 2px, transparent 2px),
          linear-gradient(to bottom, #ccc 2px, transparent 2px)
        `,
        backgroundSize: `${blockSize}px ${blockSize}px`,
        backgroundPosition: 'left bottom',
      }}
    >
      <button 
        className="absolute btn-back left-panel-btn" 
        onClick={() => swapPanel('menu', 'leftPanel')}
      >
        ⬅ Back
      </button>
      <TetrisGame panelWidth={usableWidth} />
    </div>
  );
} 